<script setup>
import Card from "@/components/ui/card/Card.vue";
import Checkbox from "@/components/ui/checkbox/Checkbox.vue";
import InputGroup from "@/components/ui/input-group/InputGroup.vue";
import InputGroupAddon from "@/components/ui/input-group/InputGroupAddon.vue";
import InputGroupInput from "@/components/ui/input-group/InputGroupInput.vue";
import Label from "@/components/ui/label/Label.vue";
import { computed, ref } from "vue";
import { Search } from "lucide-vue-next";
import GameCard from "@/components/GameCard.vue";
import { useBreakpoints, breakpointsTailwind } from "@vueuse/core";

const emit = defineEmits(["toggleInterest", "onRouteClick", "changeSearch", "dialogOpen"]);

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("lg");

const props = defineProps({
  interests: {
    type: Array,
    default: () => [],
  },
  uid: {
    type: String,
    required: true,
  },
  ownedGames: {
    type: Array,
    default: [],
  },
  search: {
    type: String,
    default: "",
  },
  profiles: {
    type: Array,
    default: []
  }
});

function findGame(game) {
  // find game in interests
  const foundGame =
    props.interests.find((interest) => interest.id == game.id) || null;
  return foundGame;
}

const sortByInterested = ref(false);
const sortByOwned = ref(false);

function isInterestedInGame(game, uid) {
  const foundGame = findGame(game);
  if (foundGame === null) return false;
  return foundGame.users && foundGame.users.includes(uid);
}

function interestedProfiles(game, userIds){
  const interestedList = ref([])
  if(userIds){
    userIds.forEach(userId => {
      if (isInterestedInGame(game, userId)){
        const matches = props.profiles.filter(a => a.id == userId)
        interestedList.value.push(...matches)
      }
    });
  }
  return interestedList
}

function toggleSortInterested() {
  sortByInterested.value = !sortByInterested.value;
}

function toggleSortOwned() {
  sortByOwned.value = !sortByOwned.value;
}

function filterByInterest(game) {
  return isInterestedInGame(game, props.uid);
}

function filterByOwned(game) {
  return props.ownedGames.some((ownedGame) => ownedGame.game.id == game.id);
}

const interestList = computed(() => {
  var filteredList = props.interests;
  if (sortByInterested.value) {
    filteredList = filteredList.filter(filterByInterest);
  }
  if (sortByOwned.value) {
    filteredList = filteredList.filter(filterByOwned);
  }

  return filteredList;
});

function setSearch(value) {
  emit("changeSearch", value);
}
</script>

<template>
  <Card
    class="border-0 rounded-0 shadow-none p-4 w-full h-full min-h-svh bg-background pb-50 lg:w-200"
  >
    <div class="flex flex-row items-center gap-4">
      <InputGroup class="grow">
        <InputGroupInput
          placeholder="Search Games..."
          @update:model-value="setSearch"
          :model-value="props.search"
        />
        <InputGroupAddon>
          <Search />
        </InputGroupAddon>
      </InputGroup>
    </div>

    <div class="flex flex-row w-full">
      <div class="m-0 w-full mr-2">
        <Label
          class="hover:bg-accent/50 hover:cursor-pointer flex items-start gap-3 rounded-lg border p-3 has-aria-checked=true:border-primary has-aria-checked=true:bg-primary/50 dark:has-aria-checked=true:border-primary/50 dark:has-[[aria-checked=true]]:bg-primary/20"
        >
          <Checkbox
            id="toggle-2"
            :default-value="sortByInterested"
            @click="toggleSortInterested"
            class="data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-white dark:data-[state=checked]:border-primary dark:data-[state=checked]:bg-primary"
          />
          <div class="grid gap-1.5 font-normal">
            <p class="text-sm leading-none font-medium">Interested Games</p>
          </div>
        </Label>
      </div>

      <div class="m-0 w-full mr-2">
        <Label
          class="hover:bg-accent/50 hover:cursor-pointer flex items-start gap-3 rounded-lg border p-3 has-aria-checked=true:border-primary has-aria-checked=true:bg-primary/50 dark:has-aria-checked=true:border-primary/50 dark:has-[[aria-checked=true]]:bg-primary/20"
        >
          <Checkbox
            id="toggle-2"
            :default-value="sortByOwned"
            @click="toggleSortOwned"
            class="data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-white dark:data-[state=checked]:border-primary dark:data-[state=checked]:bg-primary"
          />
          <div class="grid gap-1.5 font-normal">
            <p class="text-sm leading-none font-medium">Owned Games</p>
          </div>
        </Label>
      </div>
    </div>

    <div class="m-0 w-full mr-2">
      <Label v-if="!isMobile"
            class="hover:bg-accent/50 hover:cursor-pointer flex items-start gap-3 rounded-lg border p-3 has-aria-checked=true:border-primary has-aria-checked=true:bg-primary/50 dark:has-aria-checked=true:border-primary/50 dark:has-[[aria-checked=true]]:bg-primary/20 justify-center"
            @click="$emit('dialogOpen')"
          >
            <div class="font-normal">
              <p class="text-sm font-medium">Add Game</p>
            </div>
          </Label>
      <Label
        v-if="isMobile"
        class="hover:bg-accent/50 hover:cursor-pointer flex items-start gap-3 rounded-lg border p-3 has-aria-checked=true:border-primary has-aria-checked=true:bg-primary/50 dark:has-aria-checked=true:border-primary/50 dark:has-[[aria-checked=true]]:bg-primary/20 flex justify-center"
        @click="$emit('onRouteClick')"
      >
        <div class="font-normal">
          <p class="text-sm font-medium">Add Game</p>
        </div>
      </Label>
    </div>

    <div
      v-for="game in interestList"
      :key="game.name"
      class="flex flex-row items-center"
    >
      <GameCard
        :name="game.name"
        :imageURL="game.imageURL"
        :isInterested="isInterestedInGame(game, props.uid)"
        :countInterested="game?.users?.length || 0"
        @toggleInterest="$emit('toggleInterest', game)"
        :interestedProfiles="interestedProfiles(game, findGame(game)?.users).value"
      ></GameCard>
    </div>
  </Card>
</template>
